import { useEffect, useRef, useState, type PointerEvent } from 'react'

import { useI18n } from '@/shared/i18n/I18nProvider'
import { languageEvidence, primaryLanguageSubtag } from '@/shared/i18n/languageNames'
import { HINT_DELAY_MS, Tooltip } from '@/shared/ui/Tooltip'

/**
 * THE LANGUAGE A VALUE FELL BACK TO, as the few letters a reader takes in
 * without leaving the name they were reading.
 *
 * IT IS NOT A CONTROL, and that is the whole decision in this file. The
 * product's other inline mark, `shared/attachments/ValueOriginMark`, IS a
 * button — because a wand glyph says nothing at all until its sentence is
 * asked for, and a reader who cannot reach it learns nothing. This mark
 * already carries its text. Making it focusable too would buy the expansion
 * of an abbreviation at the price of a tab stop in EVERY registry row with a
 * borrowed name, in every line of an estimate, and inside the `h1` and the
 * panel titles that render a localized name — where the stop lands in the
 * middle of a heading and ahead of the panel's own close.
 *
 * SO THE SENTENCE IS IN THE MARK'S OWN TEXT, visually hidden, with the
 * abbreviation `aria-hidden` beside it. A screen reader hears "shown in
 * English (en)" wherever the value is announced — in a row, in a heading,
 * under a thumb, with no hover and nothing to reach for — and the eye reads
 * `en`. That also leaves the element nameless in the ARIA sense, which is
 * what it should be: the previous shape needed `role="note"` only to make an
 * `aria-label` legal on a span, which is a role that claims a section for two
 * characters.
 *
 * THE HINT IS THE SAME HINT THE RAIL AND THE ORIGIN MARK DRAW, after the same
 * `HINT_DELAY_MS`: a pointer travelling down a column of names would
 * otherwise flash one hint per row, which is the exact case that constant was
 * extracted for. It repeats the mark's own text and adds no second channel.
 * A touch raises no hover, and there is nothing here to tap — the sentence is
 * already in what a touch reader is read.
 *
 * ESCAPE TAKES THE HINT DOWN AND KEEPS TRAVELLING. Hover-triggered content
 * must be dismissable without moving the pointer, so the key is listened for
 * while a hint is up; it is NOT swallowed, because the pointer resting on a
 * mark says nothing about where the keyboard is, and an Escape pressed over a
 * registry row belongs to whatever the reader is actually focused in.
 */
export function LanguagePill({ language }: { language: string }) {
  const { t, locale } = useI18n()
  const [anchor, setAnchor] = useState<HTMLElement | null>(null)
  const mark = useRef<HTMLSpanElement | null>(null)
  const timer = useRef<number | null>(null)
  const sentence = t('localizedField.shownIn', { language: languageEvidence(language, locale) })

  const clearTimer = () => {
    if (timer.current !== null) {
      window.clearTimeout(timer.current)
      timer.current = null
    }
  }
  const hide = () => {
    clearTimer()
    setAnchor(null)
  }
  useEffect(() => clearTimer, [])
  useEffect(() => {
    if (anchor === null) {
      return
    }
    const dismiss = (event: KeyboardEvent) => {
      if (event.key === 'Escape') hide()
    }
    window.addEventListener('keydown', dismiss)
    return () => window.removeEventListener('keydown', dismiss)
  }, [anchor])

  const onPointerEnter = (event: PointerEvent<HTMLSpanElement>) => {
    // A touch raises no hover worth waiting on, and no press of its own.
    if (event.pointerType === 'touch') return
    clearTimer()
    timer.current = window.setTimeout(() => {
      timer.current = null
      setAnchor(mark.current)
    }, HINT_DELAY_MS)
  }

  return (
    <>
      <span
        ref={mark}
        className="localized-display__evidence"
        onPointerEnter={onPointerEnter}
        onPointerLeave={hide}
      >
        <span className="visually-hidden">{sentence}</span>
        <bdi aria-hidden="true">{primaryLanguageSubtag(language)}</bdi>
      </span>
      <Tooltip anchor={anchor} side="block-end">
        {sentence}
      </Tooltip>
    </>
  )
}
